/**
 * OPE 03 na tela — o vocabulário e as contas da conciliação, sem React.
 *
 * Os nomes que o banco grava (`reconhecida_diferente_da_producao`) não são
 * frase de interface. A tradução mora aqui, numa tabela só, para a fila, o
 * painel e o seletor de resolução dizerem a mesma coisa.
 */

export const TIPOS_DE_DIVERGENCIA = {
  horimetro_acima_do_turno: {
    rotulo: 'Horímetro acima do turno',
    explicacao: 'O horímetro andou mais do que o turno apontado: a máquina trabalhou horas que ninguém apontou.',
  },
  reconhecida_diferente_da_producao: {
    rotulo: 'Reconhecida diferente da produção',
    explicacao: 'O cliente reconheceu mais ou menos horas do que a produção apontada no dia.',
  },
  reconhecida_sem_apontamento: {
    rotulo: 'Reconhecida sem apontamento',
    explicacao: 'O cliente reconheceu horas num dia em que não há apontamento da máquina.',
  },
  producao_sem_reconhecida: {
    rotulo: 'Produção sem reconhecida',
    explicacao: 'Há produção apontada num dia que o arquivo do cliente cobre, e ele não reconheceu nada.',
  },
  /* 0103: as horas de motor da telemetria contra as do horímetro apontado. */
  telemetria_diferente_do_apontado: {
    rotulo: 'Telemetria diferente do apontado',
    explicacao: 'As horas de motor que a telemetria contou no dia não batem com as do horímetro apontado. Um dos dois está errado: confira o painel da máquina.',
  },
  telemetria_sem_apontamento: {
    rotulo: 'Telemetria sem apontamento',
    explicacao: 'A telemetria contou horas de motor num dia sem apontamento da máquina: ela trabalhou, ou ficou ligada, e ninguém apontou.',
  },
} as const;

export type TipoDeDivergencia = keyof typeof TIPOS_DE_DIVERGENCIA;

/** As que uma pessoa escolhe. `sem_divergencia_na_reapuracao` só a apuração escreve. */
export const RESOLUCOES_ESCOLHIVEIS = {
  aceita_apontamento: 'Vale o apontamento',
  aceita_reconhecida: 'Vale a hora reconhecida',
  corrigida_na_origem: 'Corrigida na origem',
  contestada_com_cliente: 'Contestada com o cliente',
} as const;

export type ResolucaoEscolhivel = keyof typeof RESOLUCOES_ESCOLHIVEIS;

const RESOLUCOES: Readonly<Record<string, string>> = {
  ...RESOLUCOES_ESCOLHIVEIS,
  sem_divergencia_na_reapuracao: 'Sumiu na reapuração',
};

export function rotuloDoTipo(tipo: string): string {
  return tipo in TIPOS_DE_DIVERGENCIA ? TIPOS_DE_DIVERGENCIA[tipo as TipoDeDivergencia].rotulo : tipo;
}

export function rotuloDaResolucao(resolucao: string | null): string {
  if (resolucao === null) return '—';
  return RESOLUCOES[resolucao] ?? resolucao;
}

export const SITUACOES_DA_FILA = ['aberta', 'resolvida'] as const;
export type SituacaoDaFila = (typeof SITUACOES_DA_FILA)[number];

export const SITUACOES_DO_LOTE: Readonly<Record<string, string>> = {
  estagio: 'Em conferência',
  promovido: 'Promovido',
  descartado: 'Descartado',
};

/** O filtro da fila, na URL (p.16): "me manda as divergências abertas de maio" é um link. */
export interface BuscaDaConciliacao {
  readonly de?: string;
  readonly ate?: string;
  readonly situacao?: SituacaoDaFila;
}

const DATA = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A URL é entrada do usuário. Valor fora do vocabulário é DESCARTADO, e não
 * corrigido no chute — a mesma regra das outras buscas do sistema.
 */
export function validarBuscaDaConciliacao(entrada: Record<string, unknown>): BuscaDaConciliacao {
  const de = entrada['de'];
  const ate = entrada['ate'];
  const situacao = entrada['situacao'];
  return {
    ...(typeof de === 'string' && DATA.test(de) ? { de } : {}),
    ...(typeof ate === 'string' && DATA.test(ate) ? { ate } : {}),
    ...(typeof situacao === 'string' && (SITUACOES_DA_FILA as readonly string[]).includes(situacao)
      ? { situacao: situacao as SituacaoDaFila }
      : {}),
  };
}

export interface LinhaDaFila {
  readonly situacao: string;
  readonly diferencaH: number;
}

/**
 * Os números do topo da fila. A diferença é somada em MÓDULO: uma
 * divergência de +2 h e outra de −2 h não se anulam — são quatro horas a
 * explicar, e não zero.
 */
export function resumirFila(linhas: readonly LinhaDaFila[]): {
  readonly abertas: number;
  readonly resolvidas: number;
  readonly horasEmAberto: number;
} {
  let abertas = 0;
  let resolvidas = 0;
  let horasEmAberto = 0;
  for (const l of linhas) {
    if (l.situacao === 'aberta') {
      abertas += 1;
      horasEmAberto += Math.abs(l.diferencaH);
    } else {
      resolvidas += 1;
    }
  }
  return { abertas, resolvidas, horasEmAberto };
}

/**
 * Os bytes do arquivo virados texto.
 *
 * O Excel em português exporta CSV em Windows-1252, e não em UTF-8. Ler tudo
 * como UTF-8 transformaria "Máquina" em "M�quina" no cabeçalho, e o arquivo
 * seria recusado por uma coluna que está lá. Tenta UTF-8 estrito primeiro; se
 * ele recusar algum byte, o arquivo é 1252.
 */
export function decodificarArquivo(bytes: ArrayBuffer): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

/** O teto do corpo que o servidor aceita, com folga: 3 MB. */
export const TAMANHO_MAXIMO_DO_ARQUIVO = 3 * 1024 * 1024;
